import { Size, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

import { fitObjectToView } from './utils';

/**
 * A hook that automatically adjusts the orthographic camera's zoom to fit a 3D object within the viewport.
 * This is useful for ensuring that models of different sizes are properly framed in the view.
 *
 * @param options
 * @param options.padding - Scale factor to add padding around the object (1.1 = 10% padding)
 * @param options.object - The THREE.Object3D to fit in view. If not provided, uses the entire scene
 * @param options.enabled - Whether the fit-to-view behavior is enabled
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const meshRef = useRef<THREE.Mesh>();
 *   useFitObjectToView({ object: meshRef.current, padding: 1.2 });
 *   return <mesh ref={meshRef}>...</mesh>;
 * }
 * ```
 */
export function useFitObjectToView({
  padding = 1.1,
  object,
  enabled = true,
}: {
  padding?: number;
  object?: THREE.Object3D;
  enabled?: boolean;
}) {
  const { camera, size } = useThree();

  useEffect(() => {
    if (!enabled || !object) return;
    fitObjectToView(object, camera, size, padding);
  }, [camera, size, object, padding, enabled]);
}

/**
 * A hook that prepares a Three.js object and its children for raycasting operations.
 * This includes setting up geometry bounds, computing normals, ensuring proper material settings,
 * and configuring raycast behavior for different types of objects (Mesh, Line, Points).
 */
export function usePrepareObjectForRaycasting(targetObject?: THREE.Object3D) {
  useEffect(() => {
    if (!targetObject) return;

    // Force update the world matrix to ensure correct transformations
    targetObject.updateMatrixWorld(true);

    // Track how many meshes we've processed
    let meshCount = 0;

    targetObject.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        meshCount++;

        // Make sure the object is visible and not frustum culled
        object.visible = true;
        object.frustumCulled = false;

        // Ensure userData exists for storing raycasting-related data
        object.userData = object.userData || {};
        object.userData.raycastable = true;
      } else if (object instanceof THREE.Line) {
        object.visible = true;
        object.frustumCulled = false;
      } else if (object instanceof THREE.Points) {
        object.visible = true;
        object.frustumCulled = false;
      }

      // Hide cartwheel skeletons
      // It should be ignored by raycasting and not be rendered
      if (object.name.endsWith('_skl')) {
        object.visible = false;
      }
    });

    console.log(`Prepared ${meshCount} meshes for raycasting`);
  }, [targetObject]); // Only re-run if targetObject changes
}

/**
 * A hook that creates and manages a ray visualization line in a Three.js scene.
 *
 * @param options
 * @param options.color - The color of the ray line
 * @param options.visible - Whether the ray should be visible
 * @param options.linewidth - The width of the ray line
 * @returns An object containing:
 *   - rayLineRef: A ref to the THREE.Line object
 *   - updateRayLine: A function to update the ray line based on a raycaster
 */
export function useRayVisualization({
  color = '#65F157',
  visible = false,
}: {
  color?: string;
  visible?: boolean;
} = {}) {
  const { scene, camera } = useThree();
  const rayLineRef = useRef<THREE.Line | null>(null);

  // Create and update the ray line
  useEffect(() => {
    // If not visible, clean up and return
    if (!visible) {
      if (rayLineRef.current) {
        scene.remove(rayLineRef.current);
        rayLineRef.current.geometry.dispose();
        const materials = [rayLineRef.current.material].flat();
        materials.forEach((material) => material.dispose());
        rayLineRef.current = null;
      }
      camera.layers.disable(1);
      return;
    }

    // Create line geometry
    const lineGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(6); // 2 points × 3 coordinates

    // Initialize with some default positions so it's not empty
    positions[0] = 0;
    positions[1] = 0;
    positions[2] = 0;
    positions[3] = 0;
    positions[4] = 0;
    positions[5] = 1;

    lineGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(positions, 3),
    );

    // Create line material
    const lineMaterial = new THREE.LineBasicMaterial({
      color,
      linewidth: 1, // Due to limitations of the OpenGL Core Profile with the WebGL renderer on most platforms linewidth will always be 1 regardless of the set value.
      transparent: false,
      opacity: 1.0,
      depthTest: false, // Make sure the ray is always visible
      depthWrite: false,
    });

    // Create line
    const line = new THREE.Line(lineGeometry, lineMaterial);
    line.visible = true; // Always visible when created
    line.renderOrder = 999; // Ensure it renders on top of other objects
    // Set the line to use layer 1 (debug layer)
    line.layers.set(1);
    // Enable camera to see layer 1
    camera.layers.enable(1);
    rayLineRef.current = line;

    // Add to scene
    scene.add(line);

    return () => {
      // Clean up
      if (rayLineRef.current) {
        scene.remove(rayLineRef.current);
        rayLineRef.current.geometry.dispose();
        const materials = [rayLineRef.current.material].flat();
        materials.forEach((material) => material.dispose());
      }
      // Disable layer 1 on cleanup
      camera.layers.disable(1);
    };
  }, [scene, camera, color, visible]);

  // Function to update the ray line
  const updateRayLine = (raycaster: THREE.Raycaster, distance = 1000) => {
    if (rayLineRef.current) {
      const rayOrigin = raycaster.ray.origin;
      const rayDirection = raycaster.ray.direction
        .clone()
        .multiplyScalar(distance); // Extend the ray
      const rayEnd = rayOrigin.clone().add(rayDirection);

      const positions = rayLineRef.current.geometry.attributes.position;
      positions.setXYZ(0, rayOrigin.x, rayOrigin.y, rayOrigin.z);
      positions.setXYZ(1, rayEnd.x, rayEnd.y, rayEnd.z);
      positions.needsUpdate = true;
    }
  };

  return { rayLineRef, updateRayLine };
}
